/**
 * Slug Validation Utility
 * 
 * Comprehensive slug validation including:
 * - Character validation (English letters, numbers, hyphens only)
 * - Lookalike character detection (Cyrillic, etc.)
 * - Length validation (3-30 characters)
 * - Auto lowercase conversion
 * - Availability checking
 * - Content moderation with OpenAI
 */

/**
 * Check if a character is a valid English letter (a-z, A-Z)
 */
function isEnglishLetter(char) {
    const code = char.charCodeAt(0);
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

/**
 * Check if a character is a digit (0-9)
 */
function isDigit(char) {
    const code = char.charCodeAt(0);
    return code >= 48 && code <= 57;
}

/**
 * Check if a character is a hyphen (-)
 */
function isHyphen(char) {
    return char === '-';
}

/**
 * Check if a character looks like an English letter but isn't (e.g., Cyrillic)
 * This prevents lookalike attacks
 */
function isLookalikeCharacter(char) {
    const code = char.charCodeAt(0);

    // Cyrillic letters that look like English letters
    // а (Cyrillic) looks like a, о looks like o, е looks like e, etc.
    const cyrillicRanges = [
        [0x0400, 0x04FF], // Cyrillic
        [0x0500, 0x052F], // Cyrillic Supplement
        [0x2DE0, 0x2DFF], // Cyrillic Extended-A
        [0xA640, 0xA69F], // Cyrillic Extended-B
    ];

    // Greek letters that might look similar
    const greekRanges = [
        [0x0370, 0x03FF], // Greek and Coptic
    ];

    // Check if character is in any of these ranges
    for (const [start, end] of [...cyrillicRanges, ...greekRanges]) {
        if (code >= start && code <= end) {
            return true;
        }
    }

    // Check for specific lookalike characters
    const lookalikes = [
        0x0430, // а (Cyrillic a)
        0x043E, // о (Cyrillic o)
        0x0435, // е (Cyrillic e)
        0x0440, // р (Cyrillic p)
        0x0441, // с (Cyrillic c)
        0x0443, // у (Cyrillic y)
        0x0445, // х (Cyrillic x)
        0x044A, // ъ (Cyrillic hard sign)
        0x044C, // ь (Cyrillic soft sign)
        0x0456, // і (Cyrillic i)
        0x03B1, // α (Greek alpha)
        0x03BF, // ο (Greek omicron)
    ];

    return lookalikes.includes(code);
}

/**
 * Validate slug format and characters
 * 
 * @param {string} slug - The slug to validate
 * @returns {{isValid: boolean, error?: string, normalizedSlug?: string}}
 */
export function validateSlugFormat(slug) {
    // 1. Trim whitespace
    const trimmed = slug.trim();

    if (!trimmed) {
        return {
            isValid: false,
            error: 'Slug cannot be empty',
        };
    }

    // 2. Convert to lowercase automatically
    const lowercased = trimmed.toLowerCase();

    // 3. Check length (3-30 characters)
    if (lowercased.length < 3) {
        return {
            isValid: false,
            error: 'Slug must be at least 3 characters long',
        };
    }

    if (lowercased.length > 30) {
        return {
            isValid: false,
            error: 'Slug cannot exceed 30 characters',
        };
    }

    // 4. Check for valid characters only (English letters, numbers, hyphens)
    for (let i = 0; i < lowercased.length; i++) {
        const char = lowercased[i];

        // Check for lookalike characters first (security check)
        if (isLookalikeCharacter(char)) {
            return {
                isValid: false,
                error: 'Only English letters (a-z), numbers (0-9), and hyphens (-) are allowed.',
            };
        }

        // Check if character is valid (English letter, digit, or hyphen)
        if (!isEnglishLetter(char) && !isDigit(char) && !isHyphen(char)) {
            return {
                isValid: false,
                error: 'Only English letters (a-z), numbers (0-9), and hyphens (-) are allowed.',
            };
        }
    }

    // 5. Check for consecutive hyphens (not allowed)
    if (lowercased.includes('--')) {
        return {
            isValid: false,
            error: 'Slug cannot contain consecutive hyphens (--).',
        };
    }

    // 6. Check that slug doesn't start or end with hyphen
    if (lowercased.startsWith('-')) {
        return {
            isValid: false,
            error: 'Slug cannot start with a hyphen (-).',
        };
    }

    if (lowercased.endsWith('-')) {
        return {
            isValid: false,
            error: 'Slug cannot end with a hyphen (-).',
        };
    }

    // All validations passed
    return {
        isValid: true,
        normalizedSlug: lowercased,
    };
}

/**
 * Check slug availability in database
 * 
 * @param {string} slug - The slug to check (should be normalized/lowercase)
 * @param {string} domain - The domain to check against
 * @param {string} userId - The user ID (for custom domain checks)
 * @param {object} supabase - Supabase client instance
 * @param {string} excludeLinkId - Optional: Link ID to exclude from the check (for edit mode)
 * @returns {Promise<{isAvailable: boolean, error?: string}>}
 */
export async function checkSlugAvailability(slug, domain, userId, supabase, excludeLinkId = null) {
    if (!supabase) {
        return {
            isAvailable: false,
            error: 'Database connection not available',
        };
    }

    // Uniqueness is per (domain + slug): domain is required
    const normalizedDomain = domain != null ? String(domain).trim().toLowerCase() : '';
    if (!normalizedDomain) {
        return {
            isAvailable: false,
            error: 'Domain is required to check slug availability.',
        };
    }

    try {
        // Normalize slug first
        const formatCheck = validateSlugFormat(slug);
        if (!formatCheck.isValid) {
            return {
                isAvailable: false,
                error: formatCheck.error,
            };
        }

        const normalizedSlug = formatCheck.normalizedSlug;

        // Check availability per (domain + slug): same slug can exist on different domains
        // If domain is "glynk.to" (default), check system-wide; if custom, check only this user
        const isDefaultDomain = normalizedDomain === 'glynk.to';

        let query = supabase
            .from('links')
            .select('id, slug, domain, user_id')
            .ilike('slug', normalizedSlug)
            .ilike('domain', normalizedDomain);

        // For default domain, check all users (system-wide)
        // For custom domain, check only this user's links
        if (!isDefaultDomain) {
            query = query.eq('user_id', userId);
        }

        // Exclude the current link ID if provided (for edit mode)
        if (excludeLinkId) {
            query = query.neq('id', excludeLinkId);
        }

        const { data: existingLinks, error } = await query.limit(1);

        if (error) {
            return {
                isAvailable: false,
                error: 'Error checking slug availability. Please try again.',
            };
        }

        if (existingLinks && existingLinks.length > 0) {
            return {
                isAvailable: false,
                error: isDefaultDomain
                    ? `This slug "${normalizedSlug}" is already taken on ${normalizedDomain}. Please choose a different slug.`
                    : `This slug "${normalizedSlug}" is already taken for your domain "${normalizedDomain}". Please choose a different slug.`,
            };
        }

        return {
            isAvailable: true,
        };
    } catch (error) {
        return {
            isAvailable: false,
            error: 'Error checking slug availability. Please try again.',
        };
    }
}

/**
 * Check slug content with Google Perspective API
 * 
 * @param {string} slug - The slug to check
 * @returns {Promise<{isSafe: boolean, error?: string, flaggedCategories?: string[]}>}
 */
/**
 * Simple cache for moderation results (to avoid repeated API calls)
 */
const moderationCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * List of blocked words/phrases - checked before API calls
 * These are words that should be blocked regardless of API results
 */
const BLOCKED_WORDS = [
    // --- GAMBLING & BETTING ---
    'gambling', 'gamble', 'casino', 'betting', 'poker', 'slots', 'roulette', 'jackpot',
    'bet', 'bet365', 'baccarat', 'lottery', 'bookie', 'wagering', 'casino-online',
    'bet-online', 'casino-bonus', 'free-spins', 'lucky-slots', 'win-money', 'make-money-fast',

    // --- ADULT CONTENT & SEXUAL ---
    'porn', 'sex', 'xxx', 'adult', 'nude', 'naked', 'erotic', 'brazers', 'hentai',
    'escort', 'camgirl', 'milf', 'playboy', 'strip', 'vixen', 'hardcore', 'softcore',
    'sexual', 'penis', 'vagina', 'clitoris', 'boobs', 'butt', 'asshole',
    '2g1c', '2 girls 1 cup', 'acrotomophilia', 'alabama hot pocket', 'alaskan pipeline',
    'anal', 'anilingus', 'anus', 'apeshit', 'arsehole', 'ass', 'assmunch',
    'auto erotic', 'autoerotic', 'babeland', 'baby batter', 'baby juice', 'ball gag',
    'ball gravy', 'ball kicking', 'ball licking', 'ball sack', 'ball sucking', 'bangbros',
    'bangbus', 'bareback', 'barely legal', 'barenaked', 'bastard', 'bastardo', 'bastinado',
    'bbw', 'bdsm', 'beaner', 'beaners', 'beaver cleaver', 'beaver lips', 'beastiality',
    'bestiality', 'big black', 'big breasts', 'big knockers', 'big tits', 'bimbos',
    'birdlock', 'bitch', 'bitches', 'black cock', 'blonde action', 'blonde on blonde action',
    'blowjob', 'blow job', 'blow your load', 'blue waffle', 'blumpkin', 'bollocks',
    'bondage', 'boner', 'boob', 'booty call', 'brown showers', 'brunette action',
    'bukkake', 'bulldyke', 'bullet vibe', 'bullshit', 'bung hole', 'bunghole',
    'busty', 'buttcheeks', 'butthole', 'camel toe', 'camslut', 'camwhore',
    'carpet muncher', 'carpetmuncher', 'chocolate rosebuds', 'cialis', 'circlejerk',
    'cleveland steamer', 'clit', 'clover clamps', 'clusterfuck', 'cock', 'cocks',
    'coprolagnia', 'coprophilia', 'cornhole', 'coon', 'coons', 'creampie', 'cum',
    'cumming', 'cumshot', 'cumshots', 'cunnilingus', 'cunt', 'darkie', 'date rape',
    'daterape', 'deep throat', 'deepthroat', 'dendrophilia', 'dick', 'dildo',
    'dingleberry', 'dingleberries', 'dirty pillows', 'dirty sanchez', 'doggie style',
    'doggiestyle', 'doggy style', 'doggystyle', 'dog style', 'dolcett', 'domination',
    'dominatrix', 'dommes', 'donkey punch', 'double dong', 'double penetration',
    'dp action', 'dry hump', 'dvda', 'eat my ass', 'ecchi', 'ejaculation',
    'erotism', 'eunuch', 'fag', 'faggot', 'fecal', 'felch', 'fellatio', 'feltch',
    'female squirting', 'femdom', 'figging', 'fingerbang', 'fingering', 'fisting',
    'foot fetish', 'footjob', 'frotting', 'fuck', 'fuck buttons', 'fuckin', 'fucking',
    'fucktards', 'fudge packer', 'fudgepacker', 'futanari', 'gangbang', 'gang bang',
    'gay sex', 'genitals', 'giant cock', 'girl on', 'girl on top', 'girls gone wild',
    'goatcx', 'goatse', 'god damn', 'gokkun', 'golden shower', 'goodpoop', 'goo girl',
    'goregasm', 'grope', 'group sex', 'g-spot', 'guro', 'hand job', 'handjob',
    'hard core', 'hardcore', 'homoerotic', 'honkey', 'hooker', 'horny', 'hot carl',
    'hot chick', 'how to kill', 'how to murder', 'huge fat', 'humping', 'incest',
    'intercourse', 'jack off', 'jail bait', 'jailbait', 'jelly donut', 'jerk off',
    'jigaboo', 'jiggaboo', 'jiggerboo', 'jizz', 'juggs', 'kike', 'kinbaku',
    'kinkster', 'kinky', 'knobbing', 'leather restraint', 'leather straight jacket',
    'lemon party', 'livesex', 'lolita', 'lovemaking', 'make me come', 'male squirting',
    'masturbate', 'masturbating', 'masturbation', 'menage a trois', 'missionary position',
    'mong', 'motherfucker', 'mound of venus', 'mr hands', 'muff diver', 'muffdiving',
    'nambla', 'nawashi', 'negro', 'neonazi', 'nigga', 'nigger', 'nig nog',
    'nimphomania', 'nipple', 'nipples', 'nsfw', 'nsfw images', 'nudity', 'nutten',
    'nympho', 'nymphomania', 'octopussy', 'omorashi', 'one cup two girls',
    'one guy one jar', 'orgasm', 'orgy', 'paedophile', 'paki', 'panties', 'panty',
    'pedobear', 'pedophile', 'pegging', 'phone sex', 'piece of shit', 'pikey',
    'pissing', 'piss pig', 'pisspig', 'pleasure chest', 'pole smoker', 'ponyplay',
    'poof', 'poon', 'poontang', 'punany', 'poop chute', 'poopchute', 'porno',
    'pornography', 'prince albert piercing', 'pthc', 'pubes', 'pussy', 'queaf',
    'queef', 'quim', 'raghead', 'raging boner', 'rape', 'raping', 'rapist',
    'rectum', 'reverse cowgirl', 'rimjob', 'rimming', 'rosy palm',
    'rosy palm and her 5 sisters', 'rusty trombone', 'sadism', 'santorum', 'scat',
    'schlong', 'scissoring', 'semen', 'sexcam', 'sexo', 'sexy', 'sexually',
    'sexuality', 'shaved beaver', 'shaved pussy', 'shemale', 'shibari', 'shit',
    'shitblimp', 'shitty', 'shota', 'shrimping', 'skeet', 'slanteye', 'slut',
    's&m', 'smut', 'snatch', 'snowballing', 'sodomize', 'sodomy', 'spastic',
    'spic', 'splooge', 'splooge moose', 'spooge', 'spread legs', 'spunk',
    'strap on', 'strapon', 'strappado', 'strip club', 'style doggy', 'suck',
    'sucks', 'suicide girls', 'sultry women', 'swastika', 'swinger', 'tainted love',
    'taste my', 'tea bagging', 'threesome', 'throating', 'thumbzilla', 'tied up',
    'tight white', 'tit', 'tits', 'titties', 'titty', 'tongue in a', 'topless',
    'tosser', 'towelhead', 'tranny', 'tribadism', 'tub girl', 'tubgirl', 'tushy',
    'twat', 'twink', 'twinkie', 'two girls one cup', 'undressing', 'upskirt',
    'urethra play', 'urophilia', 'venus mound', 'viagra', 'vibrator', 'violet wand',
    'vorarephilia', 'voyeur', 'voyeurweb', 'voyuer', 'vulva', 'wank', 'wetback',
    'wet dream', 'white power', 'whore', 'worldsex', 'wrapping men',
    'wrinkled starfish', 'xx', 'yaoi', 'yellow showers', 'yiffy', 'zoophilia', '🖕',

    // --- DRUGS & ILLICIT SUBSTANCES ---
    'drugs', 'weed', 'marijuana', 'cocaine', 'heroin', 'meth', 'mdma', 'ecstasy',
    'pills', 'lsd', 'fentanyl', 'narcotics', 'cannabis', 'hashish', 'vape', 'smoke',
    'high-times', 'psychedelic', 'dealer', 'inject', 'mj', 'high-quality-coke',

    // --- PRESCRIPTION DRUGS & PHARMACEUTICALS ---
    'valium', 'xanax', 'oxycontin', 'pharmacy-online', 'cheap-meds', 'steroids',
    'testosterone', 'weight-loss-pills',

    // --- VIOLENCE & WEAPONS ---
    'bomb', 'weapon', 'gun', 'firearm', 'shoot', 'kill', 'murder', 'terror',
    'terrorism', 'explosion', 'ammo', 'ammunition', 'grenade', 'knife', 'stab',
    'suicide', 'deadly', 'assassin', 'sniper', 'slaughter', 'massacre',

    // --- OFFENSIVE & HATE SPEECH ---
    'retard', 'racist', 'nazi', 'hitler', 'hate', 'crap',

    // --- SCAMS & FRAUD ---
    'hack', 'cracked', 'phishing', 'scam', 'fraud', 'exploit', 'malware', 'virus',
    'identity-theft', 'carding', 'spoof', 'pyramid-scheme', 'free-money',
    'hacked', 'crypto-scam', 'giveaway', 'prize-winner', 'claim-reward', 'bank-transfer',
    'western-union', 'paypal-hack',

    // --- OTHER SENSITIVE TOPICS ---
    'darknet', 'darkweb', 'tor-link', 'hitman', 'deepfake'
];

/**
 * Check if slug contains blocked words (before API calls)
 * 
 * @param {string} slug - The slug to check (with hyphens converted to spaces)
 * @returns {{isSafe: boolean, blockedWord?: string, error?: string}}
 */
function checkBlockedWords(slug) {
    // Convert slug to lowercase and split by spaces/hyphens for word matching
    const slugLower = slug.toLowerCase();
    const slugWords = slugLower.split(/[\s-]+/); // Split by spaces or hyphens

    // Build a Set for O(1) lookups of single-word blocked terms
    const singleWordBlocked = new Set(BLOCKED_WORDS.filter((w) => !w.includes(' ')));

    // Multi-word blocked phrases (contain spaces – check against full slug text)
    const multiWordBlocked = BLOCKED_WORDS.filter((w) => w.includes(' '));

    // 1. Check each slug word for an exact match against single-word blocked terms
    for (const word of slugWords) {
        if (singleWordBlocked.has(word)) {
            return {
                isSafe: false,
                blockedWord: word,
                error: 'This slug contains inappropriate content and cannot be used.',
            };
        }
    }

    // 2. Check if the full slug text (hyphens → spaces) contains any multi-word blocked phrase
    for (const phrase of multiWordBlocked) {
        if (slugLower.includes(phrase)) {
            return {
                isSafe: false,
                blockedWord: phrase,
                error: 'This slug contains inappropriate content and cannot be used.',
            };
        }
    }

    return {
        isSafe: true,
    };
}


export async function checkSlugContent(slug) {
    const perspectiveApiKey = import.meta.env.VITE_PERSPECTIVE_API_KEY;

    // Check if running on localhost (CORS issues with Perspective API)
    const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname === '');

    // Check cache first
    const cacheKey = `moderation_${slug}`;
    const cached = moderationCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.result;
    }

    // Convert hyphens to spaces for analysis (before sending to API)
    const slugForAnalysis = slug.replace(/-/g, ' ');

    // First check against blocked words list (before API calls)
    const blockedWordsCheck = checkBlockedWords(slugForAnalysis);
    if (!blockedWordsCheck.isSafe) {
        const result = {
            isSafe: false,
            error: blockedWordsCheck.error || 'This slug contains inappropriate content and cannot be used.',
            blockedByWordFilter: true,
        };
        // Cache the result
        moderationCache.set(cacheKey, { result, timestamp: Date.now() });
        return result;
    }

    if (!perspectiveApiKey) {
        const result = { isSafe: true };
        // Cache the result
        moderationCache.set(cacheKey, { result, timestamp: Date.now() });
        return result;
    }

    // Skip moderation on localhost due to CORS restrictions (do not show technical message to user)
    if (isLocalhost) {
        const result = { isSafe: true };
        moderationCache.set(cacheKey, { result, timestamp: Date.now() });
        return result;
    }

    try {
        // Perspective API endpoint
        const apiUrl = `https://commentanalyzer.googleapis.com/v1alpha1/comments:analyze?key=${perspectiveApiKey}`;

        // Request body for Perspective API
        const requestBody = {
            comment: {
                text: slugForAnalysis,
            },
            requestedAttributes: {
                TOXICITY: {},
                SEVERE_TOXICITY: {},
                IDENTITY_ATTACK: {},
                INSULT: {},
                PROFANITY: {},
                THREAT: {},
            },
            languages: ['en'], // English only for now
        };

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            if (response.status === 429) {
                const result = { isSafe: true, rateLimited: true };
                moderationCache.set(cacheKey, { result, timestamp: Date.now() });
                return result;
            }
            // For any other error, fail open (don't block the user; do not show technical message)
            const result = { isSafe: true, rateLimited: false };
            moderationCache.set(cacheKey, { result, timestamp: Date.now() });
            return result;
        }

        // Parse the successful response
        const data = await response.json();

        // Perspective API returns attribute scores (0-1, where higher = more toxic)
        // Threshold: 0.3 (30%) - if any attribute exceeds this, flag as unsafe
        const TOXICITY_THRESHOLD = 0.3;

        let result;
        const flaggedCategories = [];
        const scores = {};

        if (data.attributeScores) {
            // Check each attribute
            for (const [attribute, attributeData] of Object.entries(data.attributeScores)) {
                const score = attributeData.summaryScore?.value || 0;
                scores[attribute] = score;

                if (score >= TOXICITY_THRESHOLD) {
                    flaggedCategories.push(attribute);
                }
            }

            if (flaggedCategories.length > 0) {
                result = {
                    isSafe: false,
                    flaggedCategories,
                    error: `This slug contains inappropriate content and cannot be used.`,
                };
            } else {

                // After Perspective API passes, check with Natural Language API
                const naturalLanguageResult = await checkSlugWithNaturalLanguage(slugForAnalysis);

                if (!naturalLanguageResult.isSafe) {
                    // Natural Language API flagged the slug
                    result = {
                        isSafe: false,
                        error: naturalLanguageResult.error || `This slug contains inappropriate content and cannot be used.`,
                        sentiment: naturalLanguageResult.sentiment,
                    };
                } else {
                    // Both checks passed
                    result = {
                        isSafe: true,
                        sentiment: naturalLanguageResult.sentiment,
                    };
                }
            }
        } else {
            // No scores returned - assume safe
            result = {
                isSafe: true,
            };
        }

        // Cache the result
        moderationCache.set(cacheKey, { result, timestamp: Date.now() });
        return result;
    } catch (error) {
        // Fail open - if check fails, allow the slug (do not show technical message to user)
        return { isSafe: true };
    }
}

/**
 * Check slug content with Google Natural Language API (Content Moderation)
 * This is called after Perspective API check (adds a second gate)
 * 
 * @param {string} slug - The slug to check (with hyphens converted to spaces)
 * @returns {Promise<{isSafe: boolean, error?: string, moderation?: object}>}
 */
async function checkSlugWithNaturalLanguage(slug) {
    const naturalLanguageApiKey = import.meta.env.VITE_GOOGLE_NATURAL_LANGUAGE_API_KEY || import.meta.env.VITE_PERSPECTIVE_API_KEY;

    // Use Perspective API key if Natural Language API key not set (same Google Cloud project)
    if (!naturalLanguageApiKey) {
        return {
            isSafe: true,
        };
    }

    // Check if running on localhost (CORS issues)
    const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname === '');

    if (isLocalhost) {
        return { isSafe: true };
    }

    try {

        // Natural Language API endpoint for content moderation
        // moderateText endpoint format
        const apiUrl = `https://language.googleapis.com/v1/documents:moderateText?key=${naturalLanguageApiKey}`;

        // Add context prefix to help API understand the content better
        const contentWithContext = `the content of this page is about ${slug}`;

        // Request body for Natural Language API Content Moderation
        // Format according to Google Cloud Natural Language API documentation
        // moderateText requires: { document: { type: "PLAIN_TEXT", content: "text", language: "en" } }
        const requestBody = {
            document: {
                type: 'PLAIN_TEXT',
                content: contentWithContext, // slug with context prefix
                language: 'en',
            },
        };


        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });


        if (!response.ok) {
            // Fail open - don't block if Natural Language API fails (do not show technical message to user)
            return { isSafe: true };
        }

        // Parse the successful response
        const data = await response.json();

        // Natural Language API moderation returns moderationCategories with confidence scores (0..1)
        // Block if ANY category confidence >= 0.5 per your requirement
        const BLOCK_THRESHOLD = 0.5;
        const categories = Array.isArray(data.moderationCategories) ? data.moderationCategories : [];

        // Normalize category objects and extract confidence fields
        const normalized = categories.map((c) => {
            const name = c.name || c.category || c.label || 'unknown';
            // confidence field may be named differently; try common options
            const confidence = typeof c.confidence === 'number'
                ? c.confidence
                : (typeof c.score === 'number' ? c.score
                    : (typeof c.probability === 'number' ? c.probability : 0));
            return { name, confidence };
        });

        const blocked = normalized.filter((c) => c.confidence >= BLOCK_THRESHOLD).map((c) => c.name);

        if (blocked.length > 0) {
            return {
                isSafe: false,
                moderation: {
                    blockedCategories: blocked,
                    categories: normalized,
                },
                error: `This slug contains inappropriate content and cannot be used.`,
            };
        }

        return {
            isSafe: true,
            moderation: {
                blockedCategories: [],
                categories: normalized,
            },
        };
    } catch (error) {
        // Fail open - if check fails, allow the slug (do not show technical message to user)
        return { isSafe: true };
    }
}

/**
 * Comprehensive slug validation (format + availability + content)
 * 
 * @param {string} slug - The slug to validate
 * @param {string} domain - The domain to check against
 * @param {string} userId - The user ID
 * @param {object} supabase - Supabase client instance
 * @param {boolean} checkAvailability - Whether to check availability (default: true)
 * @param {boolean} checkContent - Whether to check content moderation (default: true)
 * @param {string} excludeLinkId - Optional: Link ID to exclude from availability check (for edit mode)
 * @returns {Promise<{isValid: boolean, error?: string, normalizedSlug?: string, isAvailable?: boolean, isContentSafe?: boolean}>}
 */
export async function validateSlug(
    slug,
    domain,
    userId,
    supabase,
    checkAvailability = true,
    checkContent = true,
    excludeLinkId = null
) {
    // 1. Format validation
    const formatCheck = validateSlugFormat(slug);
    if (!formatCheck.isValid) {
        return {
            isValid: false,
            error: formatCheck.error,
        };
    }

    const normalizedSlug = formatCheck.normalizedSlug;

    // 2. Content moderation (if enabled)
    let contentCheck = { isSafe: true };
    if (checkContent) {

        try {
            contentCheck = await checkSlugContent(normalizedSlug);
        } catch (error) {
            // If moderation check throws an error, fail open
            contentCheck = { isSafe: true, error: 'Content moderation check unavailable' };
        }

        // If content check failed due to rate limiting, skip it entirely (fail open)
        if (contentCheck.rateLimited) {
            // Skip moderation entirely - don't block the user
            // Continue with validation
        } else if (!contentCheck.isSafe) {
            // Content is actually flagged as inappropriate (not rate limited)
            return {
                isValid: false,
                error: contentCheck.error || 'Slug contains inappropriate content.',
                normalizedSlug,
                isContentSafe: false,
            };
        } else {
        }
    } else {
    }

    // 3. Availability check (if enabled) – per (domain + slug)
    let availabilityCheck = { isAvailable: true };
    if (checkAvailability) {
        const domainForCheck = domain != null ? String(domain).trim() : '';
        if (!domainForCheck) {
            return {
                isValid: false,
                error: 'Please select a domain first, then enter the slug.',
                normalizedSlug,
            };
        }
        availabilityCheck = await checkSlugAvailability(normalizedSlug, domainForCheck, userId, supabase, excludeLinkId);
        if (!availabilityCheck.isAvailable) {
            return {
                isValid: false,
                error: availabilityCheck.error || 'Slug is not available.',
                normalizedSlug,
                isAvailable: false,
            };
        }
    }

    // All checks passed
    return {
        isValid: true,
        normalizedSlug,
        isAvailable: true,
        isContentSafe: true,
    };
}

